import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tv, Moon, Wifi, CheckCircle2, Wrench } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { MenuRenderer } from "@/components/app/MenuRenderer";
import { PlaylistPlayer } from "@/components/app/PlaylistPlayer";

const DEVICE_KEY = "screensync_device_id";
// LocalStorage flag — set once the customer has been shown the "you're online"
// celebration screen so we don't re-play the 4-second transition on every
// refresh / re-init. Cleared automatically if the stick unpairs later.
const CELEBRATION_KEY = "nexso_onboarding_celebrated";

function getDeviceId() {
  // ── Priority 1: STABLE Android ID from the launcher JS bridge ──────────
  // Anchored to `Settings.Secure.ANDROID_ID` so it survives APK uninstall,
  // reinstall, and every OTA update. Without this the WebView localStorage
  // wipes on reinstall and the stick pairs with a brand-new code, leaving
  // the old screen as a ghost in the customer's fleet dashboard.
  try {
    const native = typeof window !== "undefined" ? window.NexsoNative : null;
    if (native && typeof native.getStableDeviceId === "function") {
      const stable = native.getStableDeviceId();
      if (stable && typeof stable === "string" && stable.length > 8) {
        // Cache in localStorage so a later reload without the bridge (edge
        // case: dev-tools debugger or Chromium eviction) still finds the ID.
        try { localStorage.setItem(DEVICE_KEY, stable); } catch (_) { /* noop */ }
        return stable;
      }
    }
  } catch (_) { /* bridge unavailable in browser — fall through */ }

  // ── Priority 2: previously-cached ID from localStorage ──────────────────
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    // ── Priority 3: fresh random ID (browser preview, no native launcher)
    id = "dev-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/**
 * Best-effort device telemetry. Parses the User-Agent + window.screen + the
 * modern userAgentData hints (Chromium 90+) to extract OS, OS version,
 * manufacturer, model, app version, and resolution. The launcher reports
 * these on every heartbeat so the dashboard can show device badges and the
 * OTA layer can target by hardware.
 */
function _collectDeviceInfo() {
  const ua = (navigator.userAgent || "").trim();
  const out = {
    user_agent: ua.slice(0, 256),
    platform: navigator.platform || "",
    screen_resolution: window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : "",
    app_version: APP_BUILD_VERSION,
  };
  // OS + OS version
  if (/Android\s+([\d.]+)/.test(ua)) {
    out.os = "Android";
    out.os_version = RegExp.$1;
  } else if (/iPhone OS\s+([\d_]+)/.test(ua) || /iPad.*OS\s+([\d_]+)/.test(ua)) {
    out.os = "iOS";
    out.os_version = RegExp.$1.replace(/_/g, ".");
  } else if (/Mac OS X\s+([\d_]+)/.test(ua)) {
    out.os = "macOS";
    out.os_version = RegExp.$1.replace(/_/g, ".");
  } else if (/Windows NT\s+([\d.]+)/.test(ua)) {
    out.os = "Windows";
    out.os_version = RegExp.$1;
  } else if (/CrOS/.test(ua)) {
    out.os = "ChromeOS";
  } else if (/X11.*Linux/.test(ua)) {
    out.os = "Linux";
  }
  // Manufacturer + model heuristics from the Android UA tail "; Manufacturer Model)"
  // e.g. "...; Mecool KM2 Plus Build/RP1A.200720.011)"
  if (out.os === "Android") {
    const m = ua.match(/Android\s+[\d.]+;\s*(?:[a-z-]+;\s*)?([^)]+?)(?:\s+Build|\))/i);
    if (m) {
      const parts = m[1].trim().split(/\s+/);
      if (parts.length >= 2) {
        out.manufacturer = parts[0];
        out.model = parts.slice(1).join(" ");
      } else {
        out.model = parts.join(" ");
      }
    }
  }
  // Native launcher v1.4.3+ exposes network telemetry via NexsoNative bridge.
  // Silently omitted when the app is opened in a normal browser (dev/support).
  try {
    const bridge = typeof window !== "undefined" ? window.NexsoNative : null;
    if (bridge) {
      if (typeof bridge.getLocalIp === "function") {
        const ip = bridge.getLocalIp();
        if (ip) out.local_ip = String(ip);
      }
      if (typeof bridge.getWifiSsid === "function") {
        const ssid = bridge.getWifiSsid();
        if (ssid) out.wifi_ssid = String(ssid);
      }
      if (typeof bridge.getWifiRssi === "function") {
        const rssi = bridge.getWifiRssi();
        if (typeof rssi === "number" && rssi !== 0) out.wifi_rssi = rssi;
      }
      if (typeof bridge.getLauncherVersion === "function") {
        const v = bridge.getLauncherVersion();
        if (v) out.app_version = String(v);  // native launcher version overrides web bundle
      }
      if (typeof bridge.hasInstallPermission === "function") {
        // v1.5.3+ — lets the dashboard flag sticks blocked from OTA updates.
        out.ota_allowed = !!bridge.hasInstallPermission();
      }
    }
  } catch {
    // bridge not available — normal for dev / support access
  }
  return out;
}

// Bundle version stamp so the dashboard can see which build the TV runs.
// CRA inlines REACT_APP_BUILD as a string at build time when present.
const APP_BUILD_VERSION =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BUILD) || "web";

// ── Remote Command Bridge (client-side executor) ─────────────────────────
// Whitelisted commands the dashboard can queue for this TV. Web-side ones
// execute purely in the browser. Native ones require APK v1.4.3+ which
// exposes `window.NexsoNative`. Older launchers ACK "unsupported".
async function _executeCommand(cmd, opts) {
  const { setFlashMsg, deviceId } = opts;
  const bridge = typeof window !== "undefined" ? window.NexsoNative : null;
  try {
    switch (cmd.command) {
      case "reload":
        // Ack first so the dashboard sees "done" before the reload wipes state.
        await _ackCommand(cmd, "done", "Reloading page");
        setTimeout(() => window.location.reload(), 200);
        return;
      case "clear_cache":
        try {
          localStorage.removeItem("nexso_app_version");
          sessionStorage.clear();
          if (window.caches) {
            const keys = await window.caches.keys();
            await Promise.all(keys.map((k) => window.caches.delete(k)));
          }
        } catch (e) {
          // best-effort
        }
        await _ackCommand(cmd, "done", "Cache cleared, reloading");
        setTimeout(() => window.location.reload(), 200);
        return;
      case "unpair":
        // Clear the device_id + all local state, then reload to trigger a
        // fresh pairing flow. Backend keeps the screen record (customer can
        // still unpair from the dashboard); this just resets the physical stick.
        localStorage.removeItem("screensync_device_id");
        localStorage.removeItem("nexso_onboarding_celebrated");
        await _ackCommand(cmd, "done", "Local pairing cleared");
        setTimeout(() => window.location.reload(), 300);
        return;
      case "flash_message": {
        const text = (cmd.params && cmd.params.text) || "Tech assisting…";
        const seconds = Math.max(1, Math.min(300, parseInt((cmd.params && cmd.params.duration_seconds), 10) || 15));
        setFlashMsg({ text, endsAt: Date.now() + seconds * 1000 });
        setTimeout(() => setFlashMsg(null), seconds * 1000);
        await _ackCommand(cmd, "done", `Displayed for ${seconds}s`);
        return;
      }
      // ─── Native (v1.4.3+) ────────────────────────────────────────────────
      case "relaunch_app":
        if (bridge && typeof bridge.relaunchApp === "function") {
          await _ackCommand(cmd, "done", "Relaunching launcher");
          setTimeout(() => { try { bridge.relaunchApp(); } catch { /* bridge disappeared */ } }, 200);
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+");
        return;
      case "clear_webview_cache":
        if (bridge && typeof bridge.clearWebViewCache === "function") {
          try { bridge.clearWebViewCache(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "WebView cache cleared");
          setTimeout(() => window.location.reload(), 200);
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+");
        return;
      case "open_wifi_settings":
        if (bridge && typeof bridge.openWifiSettings === "function") {
          try { bridge.openWifiSettings(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "Opened Wi-Fi settings");
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+");
        return;
      case "reboot_device":
        if (bridge && typeof bridge.rebootDevice === "function") {
          try { bridge.rebootDevice(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "Reboot triggered");
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+ (also needs root on most sticks)");
        return;
      case "capture_screenshot":
        if (bridge && typeof bridge.captureScreenshot === "function") {
          try { bridge.captureScreenshot(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "Screenshot captured");
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+");
        return;
      case "open_install_permission":
        // Opens the OS "Install unknown apps" toggle for Nexso so support can
        // remotely unblock OTA updates on sticks missing the permission.
        if (bridge && typeof bridge.openInstallPermissionSettings === "function") {
          try { bridge.openInstallPermissionSettings(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "Opened Install-unknown-apps settings on TV");
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.5.3+");
        return;
      case "force_update_check":
        // Ask the native launcher to hit /api/launcher/latest immediately,
        // bypassing its 30-minute internal poll. Fired fleet-wide by
        // Admin → "Force update all sticks" whenever a new APK is uploaded.
        if (bridge && typeof bridge.checkForUpdateNow === "function") {
          try { bridge.checkForUpdateNow(); } catch { /* bridge failed */ }
          await _ackCommand(cmd, "done", "Update check triggered");
          return;
        }
        await _ackCommand(cmd, "unsupported", "Requires launcher v1.4.3+");
        return;
      default:
        await _ackCommand(cmd, "unsupported", `Unknown command: ${cmd.command}`);
    }
  } catch (err) {
    await _ackCommand(cmd, "error", (err && err.message ? String(err.message) : "Execution error").slice(0, 400));
  }
}

async function _ackCommand(cmd, status, detail) {
  try {
    await api.post(`/screens/${cmd.screen_id}/command/${cmd.id}/ack`, { status, detail });
  } catch (e) {
    // Silent — the command will eventually TTL-expire on the server.
  }
}

export default function TVDisplay() {
  const [screen, setScreen] = useState(null);
  const [design, setDesign] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [ownerPlan, setOwnerPlan] = useState("free");
  const [powerState, setPowerState] = useState(null);
  const [error, setError] = useState("");
  // First-time onboarding celebration — brief "Step 2 · You're online" screen
  // shown for 4s when a fresh unpaired stick first reaches the backend. Kept
  // in sessionStorage so a page refresh doesn't replay it.
  const [showCelebration, setShowCelebration] = useState(false);
  // Remote "flash message" overlay — displayed when a support agent queues a
  // flash_message command from the dashboard. Auto-dismisses on the client
  // when endsAt passes.
  const [flashMsg, setFlashMsg] = useState(null);
  const deviceId = useRef(getDeviceId());
  const lastRefresh = useRef(null);
  const executedCommands = useRef(new Set());
  // Device telemetry — recomputed on every heartbeat so LAN IP / SSID stay
  // fresh if the customer switches Wi-Fi networks without rebooting. Static
  // fields (OS/model/version) are cheap so no memoization needed.
  const collectDeviceInfoFresh = () => _collectDeviceInfo();

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.post("/screens/init", {
          device_id: deviceId.current,
          device_info: collectDeviceInfoFresh(),
        });
        if (!mounted) return;

        // ---- App-version cache-bust: if backend was redeployed/restarted,
        // its app_version stamp changes. Force a hard reload through cached bundles.
        //
        // The sessionStorage "just_reloaded" flag prevents reload loops on
        // Android WebViews / multi-replica backends where the stamp can flap
        // between replicas. Each WebView session reloads at most once, no
        // matter how many version stamps it sees.
        const incomingVersion = data.app_version;
        if (incomingVersion) {
          const stored = localStorage.getItem("nexso_app_version");
          const alreadyReloaded = sessionStorage.getItem("nexso_just_reloaded") === "1";
          if (stored && stored !== incomingVersion && !alreadyReloaded) {
            localStorage.setItem("nexso_app_version", incomingVersion);
            sessionStorage.setItem("nexso_just_reloaded", "1");
            const base = window.location.href.split("?")[0].split("#")[0];
            window.location.replace(base + "?v=" + incomingVersion);
            return;
          }
          if (!stored) localStorage.setItem("nexso_app_version", incomingVersion);
        }

        const fr = data.screen?.force_refresh_at;
        if (lastRefresh.current && fr && fr !== lastRefresh.current) {
          window.location.reload();
          return;
        }
        if (fr) lastRefresh.current = fr;
        setScreen(data.screen);
        // First-online celebration — trigger a 4s "Step 2 · You're online"
        // splash the very first time a fresh stick reaches the backend and
        // is still unpaired. Uses sessionStorage so a browser refresh won't
        // replay it, and localStorage-clears once the stick is paired.
        const paired = !!data.screen?.user_id;
        if (paired) {
          localStorage.removeItem(CELEBRATION_KEY); // reset for future re-onboarding
        } else if (!localStorage.getItem(CELEBRATION_KEY)) {
          localStorage.setItem(CELEBRATION_KEY, "1");
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 4000);
        }
        setDesign(data.design || null);
        setPlaylist(data.playlist || null);
        setOwnerPlan(data.owner_plan || "free");
        setPowerState(data.power_state || null);
        setError("");
        // ── Drain remote command queue ────────────────────────────────
        // The backend marks each command 'sent' as soon as it hands it to
        // us, so no double-execution risk between polls. We still keep a
        // client-side Set to be extra-safe against duplicate handling
        // within the same session.
        const pending = Array.isArray(data.pending_commands) ? data.pending_commands : [];
        for (const cmd of pending) {
          if (!cmd || !cmd.id || executedCommands.current.has(cmd.id)) continue;
          executedCommands.current.add(cmd.id);
          _executeCommand(cmd, { setFlashMsg, deviceId: deviceId.current });
        }
      } catch (e) {
        if (!mounted) return;
        setError("Could not reach server. Retrying…");
      }
    };
    tick();
    const itv = setInterval(tick, 5000);
    return () => { mounted = false; clearInterval(itv); };
  }, []);

  // Fullscreen on first click
  const handleFullscreen = async () => {
    try {
      if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // fullscreen not supported
    }
  };

  // Ask the native launcher (via `window.NexsoNative` JS bridge) to:
  //   1. Send HDMI-CEC standby / oneTouchPlay so the TV physically powers
  //      off at closing time and back on at opening time (works on Sony,
  //      Samsung, LG, TCL, Hisense — most 2015+ smart TVs).
  //   2. Release / re-apply FLAG_KEEP_SCREEN_ON so any TV that ignores CEC
  //      still enters standby from lost video signal (fallback path).
  //   3. On the WEB side we render a full-black overlay during off-hours so
  //      the pixels themselves are OFF even if the TV can't be powered down.
  //
  // We only fire the CEC command on the EDGE (when is_operating flips), not
  // every render — otherwise the TV would keep receiving standby commands
  // and some models would refuse to be woken from the remote.
  const lastOperatingRef = useRef(null);
  useEffect(() => {
    if (!powerState) return;
    const bridge = window.NexsoNative;
    if (!bridge) return;
    const isOpen = powerState.is_operating !== false;
    // Wake lock — release when closed so signal-loss standby kicks in.
    try { bridge.setKeepScreenOn?.(isOpen); } catch { /* not native */ }
    // Detect edge — only send CEC on transition, not every poll.
    const prev = lastOperatingRef.current;
    lastOperatingRef.current = isOpen;
    if (prev === null) return;         // first observation — don't fire
    if (prev === isOpen) return;       // no change — skip
    try {
      if (isOpen) bridge.tvWake?.();   // schedule opened → wake TV
      else bridge.tvStandby?.();       // schedule closed → standby TV
    } catch { /* CEC bridge not available on this device */ }
  }, [powerState?.is_operating]);

  if (!screen) {
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div className="tv-screen flex items-center justify-center" data-testid="tv-loading">
          <div className="text-gray-500 font-body text-sm">{error || "Initializing…"}</div>
        </div>
      </>
    );
  }

  // Power schedule — when the screen is "closed" per its weekly operating
  // hours, override whatever content is assigned and show a "Closed" frame
  // (or blank if outside_hours_mode is "blank"). The launcher polls every
  // 5s so the screen flips back ON automatically when the open window starts.
  if (screen.user_id && powerState && !powerState.is_operating) {
    if (powerState.outside_hours_mode === "blank") {
      return (
        <>
          <TVFlashOverlay msg={flashMsg} />
          <div className="tv-screen bg-black" data-testid="tv-power-off" onClick={handleFullscreen} />
        </>
      );
    }
    // closed_frame — branded "We're closed · opens Mon 09:00"
    // Use the SERVER-formatted label so it matches the screen's configured
    // timezone (the dashboard shows e.g. "Opens Mon 09:00" in the customer's
    // local tz). Strip the "Closed · " prefix so we can render it as a
    // friendly subtitle below the screen name.
    const label = powerState.schedule_label || "";
    const reopenLabel = label.startsWith("Closed · ") ? label.replace(/^Closed · /, "") : null;
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div
          className="tv-screen relative flex flex-col items-center justify-center text-white overflow-hidden"
          data-testid="tv-closed-frame"
          onClick={handleFullscreen}
        >
          <div className="absolute inset-0 tv-grid-bg opacity-20" />
          <div className="relative text-center" style={{ padding: "5vh 5vw" }}>
            <div
              className="rounded-full bg-white/5 border border-white/15 flex items-center justify-center mx-auto"
              style={{ width: "12vh", height: "12vh", marginBottom: "3vh" }}
            >
              <Moon className="text-white/70" style={{ width: "6vh", height: "6vh" }} strokeWidth={1.5} />
            </div>
            <div
              className="font-bold uppercase text-white/40"
              style={{ fontSize: "1.6vh", letterSpacing: "0.32em", marginBottom: "2vh" }}
            >
              We&apos;re closed
            </div>
            <h1
              className="font-display tracking-tighter leading-none"
              style={{ fontSize: "12vh", marginBottom: "3vh" }}
            >
              {screen.name || "Closed"}
            </h1>
            {reopenLabel && (
              <p className="text-white/60 font-body" style={{ fontSize: "2vh", lineHeight: 1.4, margin: 0 }}>
                <span className="text-white font-bold">{reopenLabel}</span>
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  // Paired + has playlist → rotate through items
  if (screen.user_id && playlist) {
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div className="tv-screen fade-in" data-testid="tv-playlist" onClick={handleFullscreen}>
          <PlaylistPlayer playlist={playlist} />
          {ownerPlan !== "pro" && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/70 text-white text-xs font-bold tracking-widest uppercase pointer-events-none flex items-center gap-2" data-testid="powered-by-ribbon">
              <Tv className="h-3 w-3" strokeWidth={2.5} />
              Powered by Nexso
            </div>
          )}
        </div>
      </>
    );
  }

  // Paired + has design → show menu
  if (screen.user_id && design) {
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div className="tv-screen fade-in" data-testid="tv-displaying" onClick={handleFullscreen}>
          <MenuRenderer config={design.config} scale="tv" />
          {ownerPlan !== "pro" && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/70 text-white text-xs font-bold tracking-widest uppercase pointer-events-none flex items-center gap-2" data-testid="powered-by-ribbon">
              <Tv className="h-3 w-3" strokeWidth={2.5} />
              Powered by Nexso
            </div>
          )}
        </div>
      </>
    );
  }

  // Paired but no design assigned
  if (screen.user_id && !design && !playlist) {
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div className="tv-screen flex flex-col items-center justify-center p-12 relative" data-testid="tv-paired-no-design" onClick={handleFullscreen}>
          <div className="absolute inset-0 tv-grid-bg opacity-30" />
          <div className="relative text-center">
            <Tv className="h-12 w-12 mx-auto mb-6 text-sky-300" />
            <div className="text-[10px] font-bold uppercase tracking-widest text-sky-300 mb-3">Paired</div>
            <h1 className="font-display text-[10vw] tracking-tighter leading-none mb-4">{screen.name || "Screen"}</h1>
            <p className="text-white/60 text-xl">Waiting for a design to be published…</p>
          </div>
        </div>
      </>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // Step 2 of 3 — "You're online!" celebration. Auto-dismisses after 4s.
  // Only shown ONCE per stick lifetime (localStorage flag) so it doesn't
  // annoy customers who refresh the page or reboot the stick.
  // ═════════════════════════════════════════════════════════════════════════
  if (showCelebration && !screen?.user_id) {
    return (
      <>
        <TVFlashOverlay msg={flashMsg} />
        <div className="tv-screen relative overflow-hidden fade-in" data-testid="tv-onboarding-celebrate" onClick={handleFullscreen}>
          <div className="absolute inset-0 tv-grid-bg" style={{ opacity: 0.35 }} />
          <div className="absolute inset-0 noise-overlay" />
          {/* Radial spotlight from the checkmark for that "aha" moment */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              top: "50%", left: "50%", width: "80vh", height: "80vh",
              transform: "translate(-50%, -50%)",
              background: "radial-gradient(circle, rgba(16,185,129,0.28), transparent 60%)",
            }}
          />
          <div className="absolute" style={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <div className="h-full flex flex-col items-center justify-center px-8 text-center relative z-10">
              <div
                className="rounded-full bg-emerald-400 flex items-center justify-center"
                style={{
                  width: "18vh", height: "18vh", marginBottom: "3vh",
                  boxShadow: "0 0 60px rgba(52,211,153,0.5)",
                  animation: "fade-in 400ms ease-out",
                }}
              >
                <CheckCircle2 style={{ width: "10vh", height: "10vh" }} className="text-emerald-950" strokeWidth={2.5} />
              </div>
              <div
                className="uppercase font-bold text-emerald-300"
                style={{ fontSize: "2vh", letterSpacing: "0.32em", marginBottom: "2vh" }}
              >
                Step 2 of 3 · Connected
              </div>
              <div
                className="font-display tracking-tight text-white"
                style={{ fontSize: "9vh", lineHeight: 1, letterSpacing: "-0.02em", marginBottom: "2vh" }}
              >
                You&apos;re online!
              </div>
              <div className="text-white/60 font-body" style={{ fontSize: "2.4vh", maxWidth: "50vw", lineHeight: 1.4 }}>
                Nexso is preparing your pairing code. This will only take a moment…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // Step 3 of 3 — Pair this screen. Big code + QR + numbered instructions
  // so the customer knows EXACTLY what to do on their phone/laptop.
  // ═════════════════════════════════════════════════════════════════════════
  const code = screen.pairing_code;
  // Deep-link: scanning the QR on a phone takes the user straight to the
  // dashboard with the pairing code pre-filled.
  const pairUrl = `${window.location.origin}/dashboard/screens?code=${encodeURIComponent(code)}`;

  return (
    <>
      <TVFlashOverlay msg={flashMsg} />
      <div className="tv-screen relative overflow-hidden fade-in" data-testid="tv-pairing" onClick={handleFullscreen}>
      <div
        className="absolute tv-grid-bg"
        style={{ top: 0, right: 0, bottom: 0, left: 0, opacity: 0.4 }}
      />
      <div
        className="absolute noise-overlay"
        style={{ top: 0, right: 0, bottom: 0, left: 0 }}
      />

      {/* Top bar — wordmark left, "Step 3 of 3" badge right */}
      <div
        className="absolute flex items-center justify-between z-10"
        style={{ top: 0, left: 0, right: 0, padding: "2.5vh 3vw" }}
      >
        <div className="flex items-center">
          <div
            className="rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-[0_0_24px_rgba(14,165,233,0.6)]"
            style={{ width: "5vh", height: "5vh", marginRight: "1vw" }}
          >
            <Tv style={{ width: "3vh", height: "3vh" }} className="text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-tight" style={{ fontSize: "2.6vh", lineHeight: 1 }}>Nexso</div>
            <div className="text-white/50 font-body uppercase" style={{ fontSize: "1.4vh", letterSpacing: "0.18em" }}>Remote signage</div>
          </div>
        </div>
        <div
          className="flex items-center rounded-full bg-sky-500/15 border border-sky-400/50"
          style={{ padding: "0.9vh 1.4vw" }}
        >
          <div className="bg-sky-400 rounded-full animate-pulse" style={{ width: "1.2vh", height: "1.2vh", marginRight: "0.8vw" }} />
          <div className="text-sky-200 uppercase font-bold" style={{ fontSize: "1.5vh", letterSpacing: "0.24em" }}>
            Step 3 of 3 · Pair
          </div>
        </div>
      </div>

      {/* Main content — pairing code + numbered instructions + QR. */}
      <div
        className="absolute flex items-center justify-center"
        style={{ top: 0, right: 0, bottom: 0, left: 0 }}
      >
        <div className="flex items-center" style={{ width: "92vw", justifyContent: "center" }}>
          {/* LEFT — pairing code + numbered instructions */}
          <div style={{ paddingTop: "2vh", marginRight: "6vw", maxWidth: "56vw" }}>
            <div
              className="font-bold uppercase text-emerald-300"
              style={{ fontSize: "1.6vh", letterSpacing: "0.28em", marginBottom: "1.2vh" }}
            >
              Almost there — one more step
            </div>
            <div
              className="font-display tracking-tight leading-none text-white"
              style={{ fontSize: "5vh", marginBottom: "3.4vh", letterSpacing: "-0.02em" }}
            >
              Pair this screen<br />to your account.
            </div>

            {/* Numbered step list — big enough to read from across the room */}
            <div style={{ marginBottom: "3vh" }}>
              <div className="flex items-start" style={{ marginBottom: "1.4vh" }}>
                <div
                  className="rounded-full bg-sky-500 text-slate-950 font-bold flex items-center justify-center shrink-0"
                  style={{ width: "4vh", height: "4vh", fontSize: "2vh", marginRight: "1.4vw", marginTop: "0.2vh" }}
                >
                  1
                </div>
                <div>
                  <div className="text-white font-bold" style={{ fontSize: "2.2vh", marginBottom: "0.3vh" }}>
                    On your phone or laptop, open
                  </div>
                  <div className="text-sky-300 font-bold" style={{ fontSize: "2.6vh" }}>
                    {window.location.host}
                  </div>
                </div>
              </div>
              <div className="flex items-start" style={{ marginBottom: "1.4vh" }}>
                <div
                  className="rounded-full bg-sky-500 text-slate-950 font-bold flex items-center justify-center shrink-0"
                  style={{ width: "4vh", height: "4vh", fontSize: "2vh", marginRight: "1.4vw", marginTop: "0.2vh" }}
                >
                  2
                </div>
                <div className="text-white font-bold" style={{ fontSize: "2.2vh" }}>
                  Sign in to your Nexso account.
                </div>
              </div>
              <div className="flex items-start">
                <div
                  className="rounded-full bg-sky-500 text-slate-950 font-bold flex items-center justify-center shrink-0"
                  style={{ width: "4vh", height: "4vh", fontSize: "2vh", marginRight: "1.4vw", marginTop: "0.2vh" }}
                >
                  3
                </div>
                <div>
                  <div className="text-white font-bold" style={{ fontSize: "2.2vh", marginBottom: "0.4vh" }}>
                    Enter this code:
                  </div>
                  <div
                    className="font-display tracking-tight leading-none text-white"
                    style={{ fontSize: "12vh", letterSpacing: "-0.02em", whiteSpace: "nowrap" }}
                    data-testid="pairing-code-display"
                  >
                    {code}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — QR code shortcut */}
          <div className="flex flex-col items-center" style={{ flexShrink: 0 }}>
            <div
              className="bg-white rounded-lg shadow-2xl flex items-center justify-center"
              style={{ padding: "1.6vh", width: "28vh", height: "28vh" }}
            >
              <QRCodeSVG
                value={pairUrl}
                size={512}
                level="M"
                includeMargin={false}
                bgColor="#FFFFFF"
                fgColor="#000000"
                style={{ width: "100%", height: "100%" }}
                data-testid="pairing-qr"
              />
            </div>
            <div
              className="text-white/50 uppercase font-bold"
              style={{ fontSize: "1.4vh", marginTop: "1.4vh", letterSpacing: "0.22em" }}
            >
              Or scan with your phone
            </div>
            <div
              className="text-white/40 font-body"
              style={{ fontSize: "1.3vh", marginTop: "0.4vh", textAlign: "center", maxWidth: "26vh" }}
            >
              We&apos;ll jump you straight to the pairing step.
            </div>
          </div>
        </div>
      </div>

      {/* Bottom footer — reassurance + Wi-Fi status */}
      <div
        className="absolute flex items-center justify-center z-10"
        style={{ bottom: 0, left: 0, right: 0, padding: "2vh 3vw" }}
      >
        <div className="flex items-center text-white/50 font-body" style={{ fontSize: "1.5vh" }}>
          <Wifi style={{ width: "1.8vh", height: "1.8vh", marginRight: "0.6vw" }} className="text-emerald-400" />
          Connected · Waiting for you to enter the code on your phone.
        </div>
      </div>
      {/* Corner tag — device id (dim, doesn't distract) */}
      <div
        className="absolute text-white/25 uppercase font-bold z-10"
        style={{ right: 0, bottom: 0, padding: "2vh 3vw", fontSize: "1.1vh", letterSpacing: "0.22em" }}
      >
        Device · {deviceId.current.slice(0, 12)}
      </div>
    </div>
    </>
  );
}

// ── Remote flash message overlay ─────────────────────────────────────────
// Rendered via React Portal so it appears above every TV state (loading,
// paired-with-design, closed frame, pairing screen, etc.). Triggered by a
// `flash_message` command from the dashboard — sits on-screen for the
// configured duration then vanishes automatically.
function TVFlashOverlay({ msg }) {
  if (!msg || typeof document === "undefined") return null;
  return createPortal(
    <div
      data-testid="tv-flash-message"
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      style={{
        background: "rgba(0, 0, 0, 0.72)",
        animation: "fade-in 250ms ease-out",
      }}
    >
      <div className="text-center px-8" style={{ maxWidth: "80vw" }}>
        <div
          className="rounded-full bg-sky-500/20 border-2 border-sky-400 flex items-center justify-center mx-auto"
          style={{
            width: "14vh", height: "14vh", marginBottom: "4vh",
            boxShadow: "0 0 60px rgba(14,165,233,0.5)",
          }}
        >
          <Wrench className="text-sky-300" style={{ width: "7vh", height: "7vh" }} strokeWidth={2} />
        </div>
        <div
          className="uppercase font-bold text-sky-300"
          style={{ fontSize: "1.9vh", letterSpacing: "0.32em", marginBottom: "2.5vh" }}
        >
          Support message
        </div>
        <div
          className="font-display tracking-tight text-white"
          style={{ fontSize: "7vh", lineHeight: 1.1, letterSpacing: "-0.01em" }}
        >
          {msg.text}
        </div>
      </div>
    </div>,
    document.body,
  );
}
